import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomModal from "../modal/customModal";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import {
  isNiimbotSupported,
  isNiimbotConnected,
  connectNiimbot,
  disconnectNiimbot,
  buildTableQrLabelCanvas,
  printLabelCanvas,
  formatNiimbotConnectError,
  downloadCanvasPng,
  resolveLabelSize,
  simulatePrintDensityPreview,
  LABEL_SIZE_PRESETS,
  DEFAULT_LABEL_SIZE_ID,
  DEFAULT_PRINT_DENSITY,
} from "../../../utils/niimbotPrinter";
import "./tableQrModal.scss";

function normalizeLink(item, fallbackAreaName = "") {
  const qrUrl = item.qrUrl ?? item.QrUrl ?? "";
  const isPublishedRaw = item.isPublished ?? item.IsPublished;
  const isPublished =
    typeof isPublishedRaw === "boolean"
      ? isPublishedRaw
      : Boolean(qrUrl);
  return {
    tableId: item.tableId ?? item.TableId,
    tableName: item.tableName ?? item.TableName ?? "",
    serviceAreaId: item.serviceAreaId ?? item.ServiceAreaId,
    serviceAreaName:
      item.serviceAreaName ?? item.ServiceAreaName ?? fallbackAreaName,
    qrUrl,
    isPublished,
  };
}

function normalizeAreaTable(table, fallbackAreaName, qrById) {
  const tableId = Number(table.t_ID ?? table.T_ID ?? table.tableId ?? 0);
  const publishRaw = table.t_Publish ?? table.T_Publish ?? table.isPublished;
  const isPublished =
    typeof publishRaw === "boolean"
      ? publishRaw
      : String(publishRaw).toLowerCase() === "true";
  const fromQr = qrById.get(tableId);
  return {
    tableId,
    tableName:
      table.t_Name ?? table.T_Name ?? fromQr?.tableName ?? `Table ${tableId}`,
    serviceAreaId:
      table.sA_ID ?? table.SA_ID ?? fromQr?.serviceAreaId ?? null,
    serviceAreaName: fromQr?.serviceAreaName ?? fallbackAreaName,
    // QR only blocked when URL could not be built (e.g. missing company slug)
    qrUrl: fromQr?.qrUrl || "",
    isPublished,
  };
}

function hasPrintableQr(item) {
  return Boolean(item?.qrUrl);
}

function safeFileName(name, id) {
  return (name || `table-${id}`)
    .replace(/[^\w\-]+/g, "-")
    .toLowerCase();
}

/**
 * Table QR label studio: size, density, text options, preview, print/download scopes.
 */
function TableQrModal({
  isOpen,
  onClose,
  CompID,
  tableId = null,
  serviceAreaId = null,
  serviceAreaName = "",
  autoBrowserPrint = false,
}) {
  const authUser = useSelector((state) => state.auth.user)?.userData;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const niimSupport = useMemo(() => isNiimbotSupported(), []);

  const [loadingTables, setLoadingTables] = useState(false);
  const [links, setLinks] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [companyName, setCompanyName] = useState("");

  const [labelSizeId, setLabelSizeId] = useState(DEFAULT_LABEL_SIZE_ID);
  const [customWMm, setCustomWMm] = useState(50);
  const [customHMm, setCustomHMm] = useState(30);
  const [density, setDensity] = useState(DEFAULT_PRINT_DENSITY);
  const [showTableName, setShowTableName] = useState(true);
  const [showServiceArea, setShowServiceArea] = useState(false);
  const [showCompanyName, setShowCompanyName] = useState(true);
  const [showScanHint, setShowScanHint] = useState(false);
  const [onlyPublished, setOnlyPublished] = useState(true);

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);

  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerBusy, setPrinterBusy] = useState(false);
  const [printProgress, setPrintProgress] = useState(null);
  const [showPairGuide, setShowPairGuide] = useState(false);
  const [pairGuideIntent, setPairGuideIntent] = useState("connect");
  const [pendingPrintItems, setPendingPrintItems] = useState(null);
  const [showAllBleDevices, setShowAllBleDevices] = useState(false);

  const cancelPrintRef = useRef(false);
  const autoPrintDoneRef = useRef(false);
  const previewCanvasRef = useRef(null);
  const previewGenRef = useRef(0);
  const pairGuideRef = useRef(null);

  const rootClass = `table-qr-modal table-qr-modal--${isDarkMode ? "dark" : "light"}`;

  const activePreset = useMemo(
    () => LABEL_SIZE_PRESETS.find((p) => p.id === labelSizeId) || LABEL_SIZE_PRESETS[0],
    [labelSizeId]
  );

  const labelMm = useMemo(() => {
    if (activePreset.custom) {
      return { wMm: customWMm, hMm: customHMm };
    }
    return { wMm: activePreset.wMm, hMm: activePreset.hMm };
  }, [activePreset, customWMm, customHMm]);

  const labelPx = useMemo(
    () => resolveLabelSize(labelMm.wMm, labelMm.hMm),
    [labelMm]
  );

  const visibleLinks = useMemo(() => {
    if (onlyPublished) {
      return links.filter((l) => l.isPublished);
    }
    return links;
  }, [links, onlyPublished]);

  const printableLinks = useMemo(
    () => visibleLinks.filter((l) => hasPrintableQr(l)),
    [visibleLinks]
  );

  const previewItem = useMemo(() => {
    if (!printableLinks.length) return null;
    const selected = printableLinks.find((l) => selectedIds.has(l.tableId));
    return selected || printableLinks[0];
  }, [printableLinks, selectedIds]);

  const labelOptions = useMemo(
    () => ({
      wMm: labelMm.wMm,
      hMm: labelMm.hMm,
      showTableName,
      showServiceArea,
      showCompanyName,
      showScanHint,
      companyName,
    }),
    [
      labelMm,
      showTableName,
      showServiceArea,
      showCompanyName,
      showScanHint,
      companyName,
    ]
  );

  const loadCompanyName = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE, {
        params: { CompID },
      });
      const data = response?.data?.data ?? response?.data?.Data ?? response?.data;
      const name =
        data?.compName ??
        data?.CompName ??
        data?.companyName ??
        data?.CompanyName ??
        authUser?.compName ??
        authUser?.CompName ??
        "";
      setCompanyName(name || "");
    } catch {
      setCompanyName(authUser?.compName || authUser?.CompName || "");
    }
  }, [CompID, authUser]);

  const loadLinks = useCallback(async () => {
    if (!CompID) {
      setLinks([]);
      setSelectedIds(new Set());
      return [];
    }

    setLoadingTables(true);
    try {
      // Single selected table (from Update Dining Area row action)
      if (tableId > 0) {
        const response = await axiosInstance.get(APIs.BO_GET_TABLE_QR_LINK, {
          params: { CompID, TableID: tableId },
        });
        const data = response?.data?.data ?? response?.data?.Data;
        if (response?.data?.status === false || response?.data?.Status === false) {
          showErrorToast(
            response?.data?.message ||
              response?.data?.Message ||
              "Failed to load QR link"
          );
          setLinks([]);
          setSelectedIds(new Set());
          return [];
        }
        const link = data
          ? normalizeLink(data, serviceAreaName)
          : null;
        const list = link?.qrUrl ? [link] : [];
        setLinks(list);
        setSelectedIds(new Set(list.map((l) => l.tableId)));
        if (!list.length) {
          showErrorToast("QR link not returned from server");
        }
        return list;
      }

      // Selected service area (bulk from list or Print table QRs)
      if (serviceAreaId > 0) {
        const qrResponse = await axiosInstance.get(
          APIs.BO_GET_SERVICE_AREA_TABLE_QR_LINKS,
          { params: { CompID, SA_ID: serviceAreaId } }
        );

        if (
          qrResponse?.data?.status === false ||
          qrResponse?.data?.Status === false
        ) {
          showErrorToast(
            qrResponse?.data?.message ||
              qrResponse?.data?.Message ||
              "Failed to load QR links"
          );
          setLinks([]);
          setSelectedIds(new Set());
          return [];
        }

        const qrData =
          qrResponse?.data?.data ?? qrResponse?.data?.Data ?? [];
        const qrList = (Array.isArray(qrData) ? qrData : [])
          .map((item) => normalizeLink(item, serviceAreaName))
          .filter((item) => item.tableId);
        const qrById = new Map(qrList.map((item) => [item.tableId, item]));

        let areaTables = [];
        try {
          const areaResponse = await axiosInstance.get(
            APIs.GET_SERVICE_AREA_BY_ID,
            { params: { CompID, ID: serviceAreaId } }
          );
          const areaData =
            areaResponse?.data?.data ??
            areaResponse?.data?.Data ??
            areaResponse?.data ??
            {};
          areaTables =
            areaData.serviceAreaTables ??
            areaData.ServiceAreaTables ??
            [];
        } catch {
          areaTables = [];
        }

        let list;
        if (Array.isArray(areaTables) && areaTables.length) {
          list = areaTables
            .map((table) =>
              normalizeAreaTable(table, serviceAreaName, qrById)
            )
            .filter((item) => item.tableId > 0)
            .sort((a, b) =>
              String(a.tableName).localeCompare(String(b.tableName))
            );
        } else {
          // Fallback if area payload has no tables
          list = qrList;
        }

        setLinks(list);
        const printable = list.filter((item) => hasPrintableQr(item));
        setSelectedIds(new Set(printable.map((l) => l.tableId)));
        if (!printable.length) {
          showErrorToast("No published tables found for this dining area");
        }
        return list;
      }

      setLinks([]);
      setSelectedIds(new Set());
      return [];
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Failed to load QR links"
      );
      setLinks([]);
      setSelectedIds(new Set());
      return [];
    } finally {
      setLoadingTables(false);
    }
  }, [CompID, tableId, serviceAreaId, serviceAreaName]);

  useEffect(() => {
    if (!isOpen) {
      setLinks([]);
      setSelectedIds(new Set());
      setPreviewUrl("");
      setPrintProgress(null);
      setPrinterBusy(false);
      setShowPairGuide(false);
      setPendingPrintItems(null);
      setShowAllBleDevices(false);
      autoPrintDoneRef.current = false;
      cancelPrintRef.current = true;
      disconnectNiimbot().then(() => setPrinterConnected(false));
      return;
    }

    cancelPrintRef.current = false;
    autoPrintDoneRef.current = false;
    setPrinterConnected(isNiimbotConnected());
    loadCompanyName();
    loadLinks();
  }, [isOpen, loadCompanyName, loadLinks]);

  useEffect(() => {
    previewGenRef.current += 1;
    const thisGen = previewGenRef.current;
    let timer = null;

    const applyPreview = async () => {
      if (!previewItem?.qrUrl) {
        if (thisGen !== previewGenRef.current) return;
        setPreviewUrl("");
        setPreviewBusy(false);
        return;
      }
      setPreviewBusy(true);
      const build = () =>
        buildTableQrLabelCanvas({
          qrUrl: previewItem.qrUrl,
          tableName: previewItem.tableName,
          serviceAreaName: previewItem.serviceAreaName,
          ...labelOptions,
        });

      try {
        let canvas;
        try {
          canvas = await build();
        } catch {
          canvas = await build();
        }
        if (thisGen !== previewGenRef.current) return;
        previewCanvasRef.current = canvas;
        const previewCanvas = simulatePrintDensityPreview(canvas, density);
        setPreviewUrl(previewCanvas.toDataURL("image/png"));
      } catch {
        if (thisGen === previewGenRef.current && !previewCanvasRef.current) {
          setPreviewUrl("");
        }
      } finally {
        if (thisGen === previewGenRef.current) setPreviewBusy(false);
      }
    };

    timer = window.setTimeout(applyPreview, 120);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [previewItem, labelOptions, density]);

  const buildLabelForItem = useCallback(
    (item) =>
      buildTableQrLabelCanvas({
        qrUrl: item.qrUrl,
        tableName: item.tableName,
        serviceAreaName: item.serviceAreaName,
        ...labelOptions,
      }),
    [labelOptions]
  );

  const downloadItems = useCallback(
    async (items) => {
      if (!items?.length) {
        showErrorToast("Nothing to download");
        return;
      }
      let ok = 0;
      for (let i = 0; i < items.length; i += 1) {
        try {
          const canvas = await buildLabelForItem(items[i]);
          downloadCanvasPng(
            canvas,
            `table-qr-${safeFileName(items[i].tableName, items[i].tableId)}.png`
          );
          ok += 1;
          if (i < items.length - 1) {
            await new Promise((r) => setTimeout(r, 250));
          }
        } catch {
          // continue
        }
      }
      if (ok === 0) showErrorToast("Could not download QR images");
      else if (ok === items.length) {
        showSuccessToast(
          ok === 1 ? "QR label downloaded" : `${ok} QR labels downloaded`
        );
      } else showErrorToast(`Downloaded ${ok} of ${items.length} labels`);
    },
    [buildLabelForItem]
  );

  const browserPrintItems = useCallback(
    async (items) => {
      if (!items?.length) {
        showErrorToast("Nothing to print");
        return;
      }
      const printWindow = window.open("", "_blank", "width=900,height=1000");
      if (!printWindow) {
        showErrorToast("Pop-up blocked. Allow pop-ups to print.");
        return;
      }

      const cards = [];
      for (const item of items) {
        try {
          const canvas = await buildLabelForItem(item);
          cards.push(`
            <div class="qr-card">
              <img src="${canvas.toDataURL("image/png")}" alt="${item.tableName || "QR"}" />
              <div class="qr-table">${item.tableName || ""}</div>
              <div class="qr-area">${item.serviceAreaName || ""}</div>
            </div>
          `);
        } catch {
          // skip
        }
      }

      printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Table QR Labels</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:16px;color:#111}
  .grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:20px}
  .qr-card{border:1px solid #ddd;border-radius:8px;padding:16px;text-align:center;page-break-inside:avoid}
  .qr-card img{max-width:100%;height:auto}
  .qr-table{font-size:18px;font-weight:700;margin-top:8px}
  .qr-area{font-size:12px;color:#666}
  @media print{body{margin:0}.qr-card{break-inside:avoid}}
</style></head><body>
<div class="grid">${cards.join("")}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`);
      printWindow.document.close();
    },
    [buildLabelForItem]
  );

  const niimPrintItems = useCallback(
    async (items, client) => {
      cancelPrintRef.current = false;
      setPrintProgress({ current: 0, total: items.length });
      for (let i = 0; i < items.length; i += 1) {
        if (cancelPrintRef.current) {
          showErrorToast("Printing cancelled");
          break;
        }
        const item = items[i];
        setPrintProgress({ current: i + 1, total: items.length });
        try {
          const canvas = await buildLabelForItem(item);
          await printLabelCanvas(client, canvas, 1, { density });
        } catch (err) {
          showErrorToast(
            err?.message
              ? `Failed printing ${item.tableName || item.tableId}: ${err.message}`
              : `Failed printing ${item.tableName || item.tableId}`
          );
          return;
        }
      }
      if (!cancelPrintRef.current) {
        showSuccessToast(
          items.length === 1
            ? "Label sent to Niim Bot"
            : `${items.length} labels sent to Niim Bot`
        );
      }
    },
    [buildLabelForItem, density]
  );

  const openPairGuideForPrint = (items) => {
    if (!niimSupport.supported) {
      showErrorToast(niimSupport.message);
      return;
    }
    setPendingPrintItems(items);
    setPairGuideIntent("print");
    setShowPairGuide(true);
  };

  useEffect(() => {
    if (!showPairGuide) return undefined;
    const timer = window.setTimeout(() => {
      const guide = pairGuideRef.current;
      if (!guide) return;
      const scroller = guide.closest(".modal-body");
      if (scroller) {
        const guideTop = guide.getBoundingClientRect().top;
        const scrollerTop = scroller.getBoundingClientRect().top;
        scroller.scrollTo({
          top: scroller.scrollTop + (guideTop - scrollerTop) - 8,
          behavior: "smooth",
        });
        return;
      }
      guide.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [showPairGuide]);

  const startNiimPrint = async (items) => {
    if (!items?.length) {
      showErrorToast("Nothing to print");
      return;
    }
    if (!niimSupport.supported) {
      showErrorToast(niimSupport.message);
      return;
    }
    if (!isNiimbotConnected()) {
      openPairGuideForPrint(items);
      return;
    }
    setPrinterBusy(true);
    try {
      const client = await connectNiimbot();
      setPrinterConnected(true);
      await niimPrintItems(items, client);
    } catch (err) {
      setPrinterConnected(isNiimbotConnected());
      showErrorToast(formatNiimbotConnectError(err));
    } finally {
      setPrinterBusy(false);
      setPrintProgress(null);
    }
  };

  const handleConfirmPair = async () => {
    setPrinterBusy(true);
    try {
      const client = await connectNiimbot({
        showAllDevices: showAllBleDevices,
      });
      setPrinterConnected(true);
      setShowPairGuide(false);
      setShowAllBleDevices(false);
      showSuccessToast("Niim Bot connected");
      if (pairGuideIntent === "print" && pendingPrintItems?.length) {
        await niimPrintItems(pendingPrintItems, client);
      }
    } catch (err) {
      setPrinterConnected(isNiimbotConnected());
      showErrorToast(formatNiimbotConnectError(err));
    } finally {
      setPrinterBusy(false);
      setPrintProgress(null);
      setPendingPrintItems(null);
      setPairGuideIntent("connect");
    }
  };

  useEffect(() => {
    if (
      !isOpen ||
      !autoBrowserPrint ||
      loadingTables ||
      printableLinks.length === 0 ||
      autoPrintDoneRef.current
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      autoPrintDoneRef.current = true;
      browserPrintItems(printableLinks);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    isOpen,
    autoBrowserPrint,
    loadingTables,
    printableLinks,
    browserPrintItems,
  ]);

  const selectedItems = useMemo(
    () => visibleLinks.filter((l) => selectedIds.has(l.tableId)),
    [visibleLinks, selectedIds]
  );

  const selectedPrintableItems = useMemo(
    () => selectedItems.filter((l) => hasPrintableQr(l)),
    [selectedItems]
  );

  // Keep selection limited to currently visible tables when filter changes
  useEffect(() => {
    setSelectedIds((prev) => {
      const allowed = new Set(visibleLinks.map((l) => l.tableId));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, [visibleLinks]);

  const toggleSelect = (id) => {
    if (!visibleLinks.some((l) => l.tableId === id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () =>
    setSelectedIds(new Set(visibleLinks.map((l) => l.tableId)));
  const clearSelect = () => setSelectedIds(new Set());

  const handlePrintSelected = () => {
    if (!selectedPrintableItems.length) {
      showErrorToast(
        selectedItems.length
          ? "Selected tables have no QR link yet. Publish or fix company slug first."
          : "Nothing selected"
      );
      return;
    }
    if (selectedPrintableItems.length < selectedItems.length) {
      showErrorToast(
        `Skipped ${selectedItems.length - selectedPrintableItems.length} table(s) without a QR link`
      );
    }
    startNiimPrint(selectedPrintableItems);
  };
  const handleDownloadSelected = () => {
    if (!selectedPrintableItems.length) {
      showErrorToast(
        selectedItems.length
          ? "Selected tables have no QR link yet. Publish or fix company slug first."
          : "Nothing selected"
      );
      return;
    }
    downloadItems(selectedPrintableItems);
  };
  const handleBrowserPrintSelected = () => {
    if (!selectedPrintableItems.length) {
      showErrorToast(
        selectedItems.length
          ? "Selected tables have no QR link yet. Publish or fix company slug first."
          : "Nothing selected"
      );
      return;
    }
    browserPrintItems(selectedPrintableItems);
  };

  const handleClose = () => {
    cancelPrintRef.current = true;
    onClose?.();
  };

  const statusLabel = (() => {
    if (!niimSupport.supported) return "Niim Bot: use Chrome/Edge over HTTPS";
    if (printProgress) {
      return `Printing ${printProgress.current}/${printProgress.total}…`;
    }
    if (showPairGuide && printerBusy) return "Waiting for Bluetooth picker…";
    if (printerBusy) return "Working…";
    if (printerConnected) return "Niim Bot connected";
    return "Niim Bot not connected";
  })();

  const isSingleTable = tableId > 0;
  const singleItem = isSingleTable ? links[0] || null : null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isSingleTable ? "Table QR code" : "Table QR Codes"}
      subtitle={
        isSingleTable
          ? [serviceAreaName || singleItem?.serviceAreaName, singleItem?.tableName]
              .filter(Boolean)
              .join(" · ") || "Label print & download"
          : serviceAreaName
            ? `${serviceAreaName} · ${links.length} table${links.length === 1 ? "" : "s"}`
            : "Label print & download"
      }
      showSubmitButton={false}
      showCancelButton={true}
      isCentered={true}
      modalSize={isSingleTable ? "modal-lg" : "modal-xl"}
      maxHeight="85vh"
      dialogMaxWidth={isSingleTable ? "780px" : "960px"}
    >
      <div className={rootClass} style={{ "--tqr-theme": themeColor }}>
        <div className="table-qr-modal__studio">
          <div className="table-qr-modal__studio-main">
            <section className="table-qr-modal__label-panel">
              <div className="table-qr-modal__section-title">
                <span className="table-qr-modal__section-bar" aria-hidden />
                Label
              </div>

              <div className="table-qr-modal__size-row" role="group" aria-label="Label size">
                {LABEL_SIZE_PRESETS.map((preset) => {
                  const active = labelSizeId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`table-qr-modal__size-chip${
                        active ? " table-qr-modal__size-chip--active" : ""
                      }`}
                      onClick={() => setLabelSizeId(preset.id)}
                      disabled={printerBusy}
                    >
                      {active && <i className="fa fa-check" aria-hidden />}
                      {preset.label}
                    </button>
                  );
                })}
              </div>

              {activePreset.custom && (
                <div className="table-qr-modal__custom-size">
                  <label>
                    W (mm)
                    <input
                      type="number"
                      min={10}
                      max={100}
                      value={customWMm}
                      onChange={(e) => setCustomWMm(Number(e.target.value) || 50)}
                    />
                  </label>
                  <label>
                    H (mm)
                    <input
                      type="number"
                      min={10}
                      max={100}
                      value={customHMm}
                      onChange={(e) => setCustomHMm(Number(e.target.value) || 30)}
                    />
                  </label>
                </div>
              )}

              <div className="table-qr-modal__density">
                <span>Density</span>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={density}
                  onChange={(e) => setDensity(Number(e.target.value))}
                  disabled={printerBusy}
                  aria-label="Print density"
                />
                <strong>{density}</strong>
              </div>
              <p className="table-qr-modal__density-hint">
                Niim Bot print heat — lower looks lighter, higher darker.
              </p>
            </section>

            <section className="table-qr-modal__text-options">
              <div className="table-qr-modal__section-title">
                <span className="table-qr-modal__section-bar" aria-hidden />
                Label text
              </div>
              <div className="table-qr-modal__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={showTableName}
                    onChange={(e) => setShowTableName(e.target.checked)}
                  />
                  Table name
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showServiceArea}
                    onChange={(e) => setShowServiceArea(e.target.checked)}
                  />
                  Service area
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showCompanyName}
                    onChange={(e) => setShowCompanyName(e.target.checked)}
                  />
                  Company name
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showScanHint}
                    onChange={(e) => setShowScanHint(e.target.checked)}
                  />
                  &quot;Scan for options&quot;
                </label>
              </div>
            </section>

            {!isSingleTable && (
              <section className="table-qr-modal__table-list">
                <div className="table-qr-modal__table-list-head">
                  <span>
                    {loadingTables
                      ? "Loading…"
                      : (() => {
                          const unpublished = visibleLinks.filter(
                            (l) => !l.isPublished
                          ).length;
                          const base = `${visibleLinks.length} table${
                            visibleLinks.length === 1 ? "" : "s"
                          }`;
                          return unpublished
                            ? `${base} · ${unpublished} unpublished`
                            : base;
                        })()}
                  </span>
                  <label
                    className="table-qr-modal__published table-qr-modal__published--inline"
                    title={
                      links.some((l) => !l.isPublished)
                        ? onlyPublished
                          ? "Uncheck to list unpublished tables"
                          : "Unpublished tables can be selected and printed"
                        : "All tables in this area are published"
                    }
                  >
                    <input
                      type="checkbox"
                      checked={onlyPublished}
                      onChange={(e) => setOnlyPublished(e.target.checked)}
                      disabled={printerBusy}
                    />
                    Only published tables
                  </label>
                  <button
                    type="button"
                    className="table-qr-modal__link-btn"
                    onClick={() =>
                      selectedIds.size === visibleLinks.length &&
                      visibleLinks.length
                        ? clearSelect()
                        : selectAll()
                    }
                    disabled={!visibleLinks.length || printerBusy}
                  >
                    {selectedIds.size === visibleLinks.length &&
                    visibleLinks.length
                      ? "Clear"
                      : "Select all"}
                  </button>
                </div>
                <div className="table-qr-modal__table-rows">
                  {visibleLinks.map((item) => {
                    const canPrint = hasPrintableQr(item);
                    return (
                      <div
                        key={item.tableId}
                        className={`table-qr-modal__table-row${
                          item.isPublished
                            ? ""
                            : " table-qr-modal__table-row--unpublished"
                        }`}
                      >
                        <label className="table-qr-modal__table-row-main">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item.tableId)}
                            onChange={() => toggleSelect(item.tableId)}
                            disabled={printerBusy}
                          />
                          <span>
                            {item.tableName || `Table ${item.tableId}`}
                            {!item.isPublished ? (
                              <em className="table-qr-modal__unpublished-tag">
                                {" "}
                                Unpublished
                              </em>
                            ) : null}
                          </span>
                        </label>
                        <div className="table-qr-modal__table-row-actions">
                          <button
                            type="button"
                            title={
                              canPrint
                                ? "Download label"
                                : "QR link unavailable for this table"
                            }
                            className="table-qr-modal__icon-btn"
                            disabled={printerBusy || !canPrint}
                            onClick={() => downloadItems([item])}
                          >
                            <i className="fa fa-download" aria-hidden />
                          </button>
                          <button
                            type="button"
                            title={
                              canPrint
                                ? "Print label to Niim Bot"
                                : "QR link unavailable for this table"
                            }
                            className="table-qr-modal__icon-btn"
                            disabled={
                              printerBusy ||
                              !niimSupport.supported ||
                              !canPrint
                            }
                            onClick={() => startNiimPrint([item])}
                          >
                            <i className="fa fa-print" aria-hidden />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!loadingTables && visibleLinks.length === 0 && (
                    <div className="table-qr-modal__empty-inline">
                      {onlyPublished
                        ? "No published tables in this area."
                        : "No tables in this area."}
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          <aside className="table-qr-modal__studio-side">
            <div className="table-qr-modal__status-pill">
              <span
                className={`table-qr-modal__status-dot${
                  printerConnected ? " table-qr-modal__status-dot--ok" : ""
                }${printerBusy ? " table-qr-modal__status-dot--busy" : ""}`}
              />
              {statusLabel}
            </div>

            <div className="table-qr-modal__preview-card">
              <div className="table-qr-modal__preview-title">
                Label preview
                <span>
                  {labelMm.wMm} × {labelMm.hMm} mm · {labelPx.wPx}×{labelPx.hPx} px
                  · density {density}
                </span>
              </div>
              <div className="table-qr-modal__preview-frame">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Label preview"
                    className={
                      previewBusy ? "table-qr-modal__preview-img--busy" : undefined
                    }
                  />
                ) : previewBusy ? (
                  <div className="table-qr-modal__spinner" />
                ) : (
                  <p className="table-qr-modal__empty-text mb-0">No preview</p>
                )}
                {previewBusy && previewUrl && (
                  <div className="table-qr-modal__preview-busy" aria-hidden>
                    <div className="table-qr-modal__spinner" />
                  </div>
                )}
              </div>
            </div>

            <div className="table-qr-modal__side-actions">
              {printerConnected ? (
                <button
                  type="button"
                  className="table-qr-modal__btn table-qr-modal__btn--ghost"
                  disabled={printerBusy}
                  onClick={async () => {
                    setPrinterBusy(true);
                    try {
                      await disconnectNiimbot();
                      setPrinterConnected(false);
                      showSuccessToast("Niim Bot disconnected");
                    } catch (err) {
                      showErrorToast(formatNiimbotConnectError(err));
                    } finally {
                      setPrinterBusy(false);
                    }
                  }}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="table-qr-modal__btn table-qr-modal__btn--outline"
                  disabled={!niimSupport.supported || printerBusy}
                  onClick={() => {
                    setPairGuideIntent("connect");
                    setPendingPrintItems(null);
                    setShowPairGuide(true);
                  }}
                >
                  Connect Niim Bot
                </button>
              )}
            </div>
          </aside>
        </div>

        {showPairGuide && (
          <div
            ref={pairGuideRef}
            className="table-qr-modal__pair-guide"
            role="region"
            aria-label="Pair Niim Bot"
          >
            <div className="table-qr-modal__pair-guide-icon" aria-hidden>
              <i className="fa fa-bluetooth-b" />
            </div>
            <div className="table-qr-modal__pair-guide-body">
              <div className="table-qr-modal__pair-guide-title">
                {pairGuideIntent === "print"
                  ? "Connect printer to print labels"
                  : "Connect your Niim Bot"}
              </div>
              <p className="table-qr-modal__pair-guide-text">
                Power on the printer and keep it nearby. Chrome will open a
                Bluetooth picker — choose your Niim Bot (often named{" "}
                <strong>B1…</strong> or <strong>K3…</strong>).
              </p>
              <label className="table-qr-modal__published">
                <input
                  type="checkbox"
                  checked={showAllBleDevices}
                  disabled={printerBusy}
                  onChange={(e) => setShowAllBleDevices(e.target.checked)}
                />
                Printer not listed? Show all Bluetooth devices
              </label>
              <div className="table-qr-modal__pair-guide-actions">
                <button
                  type="button"
                  className="table-qr-modal__btn table-qr-modal__btn--soft"
                  disabled={printerBusy}
                  onClick={() => {
                    setShowPairGuide(false);
                    setPendingPrintItems(null);
                    setShowAllBleDevices(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="table-qr-modal__btn table-qr-modal__btn--primary"
                  disabled={printerBusy}
                  onClick={handleConfirmPair}
                >
                  {printerBusy ? "Waiting…" : "Open Bluetooth picker"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-qr-modal__footer-actions">
          {isSingleTable ? (
            <>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--primary"
                disabled={printerBusy || !singleItem || !niimSupport.supported}
                onClick={() => singleItem && startNiimPrint([singleItem])}
              >
                Print to Niim Bot
              </button>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--outline"
                disabled={printerBusy || !singleItem}
                onClick={() => singleItem && downloadItems([singleItem])}
              >
                Download
              </button>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--soft"
                disabled={printerBusy || !singleItem}
                onClick={() => singleItem && browserPrintItems([singleItem])}
              >
                Print (browser)
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--outline"
                disabled={printerBusy || !selectedPrintableItems.length || !niimSupport.supported}
                onClick={handlePrintSelected}
              >
                Print selected
              </button>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--outline"
                disabled={printerBusy || !selectedPrintableItems.length}
                onClick={handleDownloadSelected}
              >
                Download selected
              </button>
              <button
                type="button"
                className="table-qr-modal__btn table-qr-modal__btn--outline"
                disabled={printerBusy || !selectedPrintableItems.length}
                onClick={handleBrowserPrintSelected}
              >
                Print selected (browser)
              </button>
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
}

export default TableQrModal;
